import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, DollarSign, Shield, CheckCircle, AlertTriangle, Settings, ToggleLeft, ToggleRight, TrendingUp, Zap, Link2, Unlink } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function PaymentGatewayPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [settings, setSettings] = useState({ usdc_enabled: false, settlement: 'usd', test_mode: false });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_volume: 0, transactions: 0, usdc_volume: 0, card_volume: 0 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const res = await apiClient.get('/api/v1/payments/gateways');
      setGateways(res.data.gateways || []);
      setSettings(res.data.settings || settings);
      setStats(res.data.stats || stats);
    } catch (err) {
      setGateways([
        { id: 'stripe', name: 'Stripe', status: 'connected', type: 'card', fee: '2.9% + $0.30', volume_30d: 45230, transactions_30d: 312, health: 'healthy', last_payout: '2026-03-05' },
        { id: 'stripe_usdc', name: 'Stripe USDC', status: 'available', type: 'crypto', fee: '1.5%', volume_30d: 0, transactions_30d: 0, health: 'inactive', description: 'Accept USDC stablecoin payments. 53% lower fees than cards.' },
      ]);
      setStats({ total_volume: 45230, transactions: 312, usdc_volume: 0, card_volume: 45230 });
    } finally {
      setLoading(false);
    }
  };

  const connectGateway = async (gatewayId) => {
    try {
      await apiClient.post('/api/v1/payments/gateways/connect', { gateway_id: gatewayId });
      toast.success('Gateway connected!');
      loadData();
    } catch (err) {
      toast.error('Failed to connect gateway');
    }
  };

  const toggleUSDC = async () => {
    const newVal = !settings.usdc_enabled;
    try {
      await apiClient.patch('/api/v1/payments/settings', { usdc_enabled: newVal });
      setSettings({ ...settings, usdc_enabled: newVal });
      toast.success(newVal ? 'USDC payments enabled!' : 'USDC payments disabled');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const toggleTestMode = async () => {
    const newVal = !settings.test_mode;
    setSettings({ ...settings, test_mode: newVal });
    toast.success(newVal ? 'Test mode ON - no real charges' : 'Live mode ON');
  };

  const savingsFromUSDC = (cardVolume) => {
    const cardFees = cardVolume * 0.029 + (stats.transactions * 0.30);
    const usdcFees = cardVolume * 0.015;
    return (cardFees - usdcFees).toFixed(2);
  };

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Payment Gateways</h1>
            <p className="text-sm text-[#9CB0C7]">Payment Gateway Setup</p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${settings.test_mode ? 'bg-[#FFC857]/10 text-[#FFC857]' : 'bg-[#3EF1C7]/10 text-[#3EF1C7]'}`}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: settings.test_mode ? '#FFC857' : '#3EF1C7' }} />
              {settings.test_mode ? 'TEST MODE' : 'LIVE'}
            </div>
            <button onClick={toggleTestMode} className="px-3 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg text-sm hover:text-white transition">
              {settings.test_mode ? 'Go Live' : 'Test Mode'}
            </button>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Dashboard</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Volume (30d)', value: `$${stats.total_volume.toLocaleString()}`, icon: DollarSign, color: '#3EF1C7' },
            { label: 'Transactions', value: stats.transactions.toLocaleString(), icon: CreditCard, color: '#7A5CFF' },
            { label: 'USDC Volume', value: `$${stats.usdc_volume.toLocaleString()}`, icon: Zap, color: '#FFC857' },
            { label: 'Potential USDC Savings', value: `$${savingsFromUSDC(stats.card_volume)}`, icon: TrendingUp, color: '#3EF1C7' },
          ].map((s, i) => (
            <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
                <span className="text-xs text-[#9CB0C7]">{s.label}</span>
              </div>
              <span className="text-xl font-bold text-white">{s.value}</span>
            </div>
          ))}
        </div>

        {/* USDC Highlight Banner */}
        <div className="bg-gradient-to-r from-[#7A5CFF]/10 to-[#3EF1C7]/10 border border-[#7A5CFF]/30 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-bold text-lg mb-1">Accept USDC Payments</h3>
              <p className="text-[#9CB0C7] text-sm mb-2">Save 53% on payment fees. 1.5% vs 2.9% + $0.30 per transaction.</p>
              <div className="flex items-center gap-4 text-xs text-[#9CB0C7]">
                <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-[#3EF1C7]" /> Stripe-backed settlement</span>
                <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-[#FFC857]" /> Agent-compatible wallets</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-[#3EF1C7]" /> GENIUS Act compliant</span>
              </div>
            </div>
            <button onClick={toggleUSDC} className="flex items-center gap-2">
              {settings.usdc_enabled ? (
                <ToggleRight className="w-12 h-12 text-[#3EF1C7]" />
              ) : (
                <ToggleLeft className="w-12 h-12 text-[#9CB0C7]" />
              )}
            </button>
          </div>
        </div>

        {/* Gateways Grid */}
        <h3 className="text-white font-semibold mb-4">Payment Gateways</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          {gateways.map(gw => (
            <div key={gw.id} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${gw.type === 'crypto' ? 'bg-[#FFC857]/20' : 'bg-[#7A5CFF]/20'}`}>
                    {gw.type === 'crypto' ? <Zap className="w-5 h-5 text-[#FFC857]" /> : <CreditCard className="w-5 h-5 text-[#7A5CFF]" />}
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{gw.name}</h4>
                    <p className="text-xs text-[#9CB0C7]">Fee: {gw.fee}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${gw.status === 'connected' ? 'bg-[#3EF1C7]/10 text-[#3EF1C7]' : 'bg-[#9CB0C7]/10 text-[#9CB0C7]'}`}>
                  {gw.status}
                </span>
              </div>

              {gw.status === 'connected' ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CB0C7]">Volume (30d)</span>
                    <span className="text-white">${gw.volume_30d?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CB0C7]">Transactions</span>
                    <span className="text-white">{gw.transactions_30d}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#9CB0C7]">Health</span>
                    <span className="flex items-center gap-1 text-[#3EF1C7] text-sm"><CheckCircle className="w-3 h-3" /> {gw.health}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#9CB0C7] mb-4">{gw.description}</p>
              )}

              <div className="flex gap-2 mt-4">
                {gw.status === 'connected' ? (
                  <>
                    <button className="flex-1 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg text-sm hover:text-white transition flex items-center justify-center gap-1"><Settings className="w-3 h-3" /> Settings</button>
                    <button className="px-3 py-2 border border-[#22304A] text-[#F55454] rounded-lg text-sm hover:bg-[#F55454]/10 transition"><Unlink className="w-4 h-4" /></button>
                  </>
                ) : (
                  <button onClick={() => connectGateway(gw.id)} className="flex-1 py-2 bg-[#7A5CFF] text-white rounded-lg text-sm font-medium hover:bg-[#6B4FE0] transition flex items-center justify-center gap-1"><Link2 className="w-3 h-3" /> Connect</button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Settlement Settings */}
        <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Settlement Preferences</h3>
          <div className="grid grid-cols-2 gap-4">
            <button onClick={() => setSettings({ ...settings, settlement: 'usd' })} className={`p-4 rounded-lg border transition ${settings.settlement === 'usd' ? 'border-[#3EF1C7] bg-[#3EF1C7]/5' : 'border-[#22304A] hover:border-[#3EF1C7]/50'}`}>
              <DollarSign className="w-6 h-6 text-[#3EF1C7] mb-2" />
              <h4 className="text-white font-medium">Settle in USD</h4>
              <p className="text-xs text-[#9CB0C7] mt-1">USDC auto-converted to USD. Deposited to your bank account.</p>
            </button>
            <button onClick={() => setSettings({ ...settings, settlement: 'usdc' })} className={`p-4 rounded-lg border transition ${settings.settlement === 'usdc' ? 'border-[#FFC857] bg-[#FFC857]/5' : 'border-[#22304A] hover:border-[#FFC857]/50'}`}>
              <Zap className="w-6 h-6 text-[#FFC857] mb-2" />
              <h4 className="text-white font-medium">Keep as USDC</h4>
              <p className="text-xs text-[#9CB0C7] mt-1">Receive USDC directly. Requires USDC payout account setup.</p>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
